'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Office {
  id: number;
  country: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  coordinates: { lat: number; lng: number };
  type: 'headquarters' | 'regional' | 'branch';
  services: string[];
}

interface OfficesSectionProps {
  className?: string;
}

const OfficesSection = ({ className = '' }: OfficesSectionProps) => {
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);

  const offices: Office[] = [
    {
      id: 1,
      country: 'Kenya',
      city: 'Nairobi',
      address: 'Westlands Business Park, Waiyaki Way, Nairobi',
      phone: '+243981944976',
      email: 'mylesdrivelc@gmail.com',
      coordinates: { lat: -1.2921, lng: 36.8219 },
      type: 'headquarters',
      services: ['Fleet Management', 'Operations Center', 'Customer Service', 'Technology Hub']
    },
    {
      id: 2,
      country: 'DRC',
      city: 'Lubumbashi',
      address: 'Avenue Kasa-Vubu, Lubumbashi, Haut-Katanga',
      phone: '+243981944976',
      email: 'mylesdrivelc@gmail.com',
      coordinates: { lat: -11.6667, lng: 27.4667 },
      type: 'regional',
      services: ['Mining Logistics', 'Heavy Equipment', 'Customs Clearance', 'Security Services']
    },
    {
      id: 3,
      country: 'Tanzania',
      city: 'Dar es Salaam',
      address: 'Msasani Peninsula, Dar es Salaam',
      phone: '+243981944976',
      email: 'mylesdrivelc@gmail.com',
      coordinates: { lat: -6.7924, lng: 39.2083 },
      type: 'regional',
      services: ['Port Logistics', 'Cross-Border Transport', 'Warehousing', 'Documentation']
    },
    {
      id: 4,
      country: 'Uganda',
      city: 'Kampala',
      address: 'Nakasero Road, Kampala',
      phone: '+243981944976',
      email: 'mylesdrivelc@gmail.com',
      coordinates: { lat: 0.3476, lng: 32.5825 },
      type: 'branch',
      services: ['Regional Distribution', 'Fleet Services', 'Customer Support']
    },
    {
      id: 5,
      country: 'Rwanda',
      city: 'Kigali',
      address: 'KG 7 Avenue, Kigali',
      phone: '+243981944976',
      email: 'mylesdrivelc@gmail.com',
      coordinates: { lat: -1.9536, lng: 30.0606 },
      type: 'branch',
      services: ['Transit Services', 'Documentation', 'Customer Support']
    },
    {
      id: 6,
      country: 'Burundi',
      city: 'Bujumbura',
      address: 'Avenue de la Mission, Bujumbura',
      phone: '+243981944976',
      email: 'mylesdrivelc@gmail.com',
      coordinates: { lat: -3.3614, lng: 29.3599 },
      type: 'branch',
      services: ['Cross-Border Services', 'Local Distribution', 'Support Center']
    }
  ];

  const getOfficeTypeLabel = (type: string) => {
    switch (type) {
      case 'headquarters':
        return 'Headquarters';
      case 'regional':
        return 'Regional Office';
      case 'branch':
        return 'Branch Office';
      default:
        return 'Office';
    }
  };

  const getOfficeTypeColor = (type: string) => {
    switch (type) {
      case 'headquarters':
        return 'bg-accent text-primary';
      case 'regional':
        return 'bg-secondary text-white';
      case 'branch':
        return 'bg-muted text-foreground';
      default:
        return 'bg-muted text-foreground';
    }
  };

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="BuildingOfficeIcon" size={20} className="text-secondary" />
            <span className="font-body text-sm font-semibold text-secondary">Regional Presence</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Our Offices Across Africa
          </h2>
          <p className="font-body text-lg text-muted-foreground">
            Strategically located across six African nations to serve you better with local expertise and regional connectivity.
          </p>
        </div>

        {/* Offices Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {offices.map((office) => (
            <div
              key={office.id}
              className="bg-card rounded-xl p-6 shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-300 border border-border cursor-pointer group"
              onClick={() => setSelectedOffice(office)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="MapPinIcon" size={20} className="text-secondary" />
                    <h3 className="font-headline font-bold text-xl text-foreground">{office.city}</h3>
                  </div>
                  <p className="font-body text-sm text-muted-foreground">{office.country}</p>
                </div>
                <span className={`font-body text-xs px-3 py-1 rounded-full ${getOfficeTypeColor(office.type)}`}>
                  {getOfficeTypeLabel(office.type)}
                </span>
              </div>

              {/* Address */}
              <div className="space-y-3 mb-4">
                <div className="flex items-start space-x-3">
                  <Icon name="BuildingOfficeIcon" size={16} className="text-muted-foreground mt-1 flex-shrink-0" />
                  <p className="font-body text-sm text-muted-foreground">{office.address}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="PhoneIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                  <p className="font-body text-sm text-muted-foreground">{office.phone}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="EnvelopeIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                  <p className="font-body text-sm text-muted-foreground">{office.email}</p>
                </div>
              </div>

              {/* Services */}
              <div className="pt-4 border-t border-border">
                <p className="font-body text-xs font-semibold text-muted-foreground mb-2">Services Available:</p>
                <div className="flex flex-wrap gap-2">
                  {office.services.slice(0, 2).map((service, index) => (
                    <span key={index} className="font-body text-xs bg-muted px-2 py-1 rounded text-muted-foreground">
                      {service}
                    </span>
                  ))}
                  {office.services.length > 2 && (
                    <span className="font-body text-xs text-secondary">+{office.services.length - 2} more</span>
                  )}
                </div>
              </div>

              {/* View Details */}
              <button className="mt-4 flex items-center space-x-2 text-secondary hover:text-accent transition-colors duration-300 font-body text-sm font-semibold group-hover:translate-x-1 transition-transform">
                <span>View on Map</span>
                <Icon name="ArrowRightIcon" size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Map Modal */}
        {selectedOffice && (
          <div
            className="fixed inset-0 bg-primary/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedOffice(null)}
          >
            <div
              className="bg-card rounded-2xl max-w-4xl w-full shadow-elevation-5 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary to-primary/90 p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-headline font-bold text-2xl mb-2">{selectedOffice.city}, {selectedOffice.country}</h3>
                    <p className="font-body text-sm text-white/80">{selectedOffice.address}</p>
                  </div>
                  <button
                    onClick={() => setSelectedOffice(null)}
                    className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors duration-300"
                  >
                    <Icon name="XMarkIcon" size={24} />
                  </button>
                </div>
              </div>

              {/* Map */}
              <div className="relative h-96 bg-muted">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`${selectedOffice.city} Office Location`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${selectedOffice.coordinates.lat},${selectedOffice.coordinates.lng}&z=14&output=embed`}
                  className="w-full h-full"
                />
              </div>

              {/* Office Details */}
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-headline font-bold text-lg text-foreground mb-3">Contact Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <Icon name="PhoneIcon" size={18} className="text-secondary" />
                        <span className="font-body text-sm text-muted-foreground">{selectedOffice.phone}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Icon name="EnvelopeIcon" size={18} className="text-secondary" />
                        <span className="font-body text-sm text-muted-foreground">{selectedOffice.email}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-headline font-bold text-lg text-foreground mb-3">Services Available</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedOffice.services.map((service, index) => (
                        <span key={index} className="font-body text-xs bg-accent/10 text-secondary px-3 py-1 rounded-lg border border-accent/20">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OfficesSection;